import React from 'react';
import { format as formatDateFns } from 'date-fns';
import './PrintableStructureAvailability.css';

interface Structure {
  id: number;
  code: string;
  type: string;
  name: string;
  address: string;
  size: string;
  currentDailyPrice: number | null;
  currentMonthlyPrice: number | null;
  imageUrl?: string; // Added for structure image
}

interface PrintableStructureAvailabilityProps {
  structures: Structure[];
  dateRange: [Date | null, Date | null];
  selectedType: string;
  searchTerm: string;
  companyName?: string; // Optional: for company name in header/footer
  companyDetails?: string; // Optional: for company details
}

const PrintableStructureAvailability: React.FC<PrintableStructureAvailabilityProps> = ({
  structures,
  dateRange,
  selectedType,
  searchTerm,
  companyName = 'Ваша Компания', // Default company name
  companyDetails = 'Контактная информация' // Default company details
}) => {
  const formatDisplayDate = (date: Date | null): string => {
    if (!date) return '...';
    return formatDateFns(date, 'dd.MM.yyyy');
  };

  const formatDisplayRange = (): string => {
    const [start, end] = dateRange;
    if (start && end) {
      return `с ${formatDisplayDate(start)} по ${formatDisplayDate(end)}`;
    } else if (start) {
      return `с ${formatDisplayDate(start)}`;
    }
    return 'Период не указан';
  };

  const formatCurrency = (amount: number | null): string => {
    if (amount === null || isNaN(amount)) return '-';
    return new Intl.NumberFormat('ru-KZ', {
      style: 'currency',
      currency: 'KZT',
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const isCityboard = (structureType: string) => structureType === 'Ситиборд';

  const formatDescription = (description: string): React.ReactNode => {
    if (!description) return '-';
    
    const urlRegex = /(https?:\/\/[^\s]+)/gi;
    
    const urlParts = description.split(urlRegex);
    
    const processedParts = urlParts.map((part, index) => {
      if (urlRegex.test(part)) {
        return (
          <a 
            key={`url-${index}`}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className="description-link"
          >
            {part}
          </a>
        );
      }
      
      return formatBoldWords(part, index);
    });
    
    return processedParts;
  };

  const formatBoldWords = (text: string, keyPrefix: number): React.ReactNode => {
    if (!text) return text;
    
    const boldPatterns = [
      { pattern: /(2gis)/i, found: false },
      { pattern: /(2гис)/i, found: false },
      { pattern: /(Google)/i, found: false },
      { pattern: /(Гугл)/i, found: false }
    ];
    
    let result: React.ReactNode[] = [text];
    
    boldPatterns.forEach((boldPattern, patternIndex) => {
      const newResult: React.ReactNode[] = [];
      
      result.forEach((node, nodeIndex) => {
        if (typeof node === 'string') {
          const match = node.match(boldPattern.pattern);
          
          if (match && !boldPattern.found) {
            const firstOccurrence = match[0];
            const index = node.toLowerCase().indexOf(firstOccurrence.toLowerCase());
            
            if (index !== -1) {
              const before = node.substring(0, index);
              const after = node.substring(index + firstOccurrence.length);
              
              boldPatterns[patternIndex].found = true;
              
              newResult.push(
                before,
                <span key={`bold-${keyPrefix}-${patternIndex}-${nodeIndex}`} className="bold-words">
                  {firstOccurrence}
                </span>,
                after
              );
            } else {
              newResult.push(node);
            }
          } else {
            newResult.push(node);
          }
        } else {
          newResult.push(node);
        }
      });
      
      result = newResult;
    });
    
    return result.length === 1 ? result[0] : <>{result}</>;
  };

  return (
    <div className="printable-container p-4">
      <header className="printable-header text-center mb-4">
        <h1 className="text-2xl font-bold">Отчет о свободных конструкциях</h1>
        <p className="text-sm">Период: {formatDisplayRange()}</p>
        <p className="text-sm">Цены указаны в тенге с учетом НДС.</p>
        {selectedType && selectedType !== 'all' && (
          <p className="text-sm">Тип конструкции: {selectedType}</p>
        )}
        {searchTerm && (
          <p className="text-sm">Поиск: {searchTerm}</p>
        )}
      </header>

      {structures.length > 0 ? (
        (() => {
          const groupedStructures: { [key: string]: Structure[] } = {};
          if (selectedType && selectedType !== 'all') {
            groupedStructures[selectedType] = structures;
          } else {
            structures.forEach(structure => {
              if (!groupedStructures[structure.type]) {
                groupedStructures[structure.type] = [];
              }
              groupedStructures[structure.type].push(structure);
            });
          }

          const sortedTypes = Object.keys(groupedStructures).sort();

          return sortedTypes.map(type => (
            <div key={type} className="type-group">
              <h2 className="type-heading">{type}</h2>
              <div className="cards-container">
                {groupedStructures[type].map((structure) => (
                  <div key={structure.id} className="structure-card">
                    {structure.imageUrl ? (
                      <img
                        src={structure.imageUrl}
                        alt={structure.name || 'Structure Image'}
                        className="card-image"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          const placeholder = document.createElement('div');
                          placeholder.className = 'image-placeholder';
                          placeholder.textContent = 'Фото не доступно';
                          target.parentNode?.insertBefore(placeholder, target.nextSibling);
                        }}
                      />
                    ) : (
                      <div className="image-placeholder">Нет фото</div>
                    )}
                    <div className="card-details">
                      <h3 className="card-name">{structure.code || 'Без названия'}</h3>
                      {/* Type is now in the group heading, can be removed from card if desired */}
                      <p className="card-info"><strong>Тип:</strong> {structure.type}</p> 
                      <p className="card-info"><strong>Адрес:</strong> {structure.address}</p>
                      <p className="card-info"><strong>Размер:</strong> {structure.size || '-'}</p>
                      <p className="card-info">
                        <strong>Описание:</strong><br />
                        <span className="description-text">{formatDescription(structure.name)}</span>
                      </p> 
                      <div className="card-pricing">
                        {!isCityboard(structure.type) && (
                          <p><strong>День:</strong> {formatCurrency(structure.currentDailyPrice)}</p>
                        )}
                        <p><strong>Месяц:</strong> {formatCurrency(structure.currentMonthlyPrice)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ));
        })()
      ) : (
        <div className="no-results-message text-center p-6 bg-white rounded-lg shadow">
          <p className="text-gray-500">
            Свободные конструкции, соответствующие критериям, не найдены.
          </p>
        </div>
      )}

      <footer className="printable-footer mt-8 text-xs text-gray-600">
        <div className="flex justify-between">
          <p>Отчет сгенерирован: {formatDateFns(new Date(), 'dd.MM.yyyy HH:mm')}</p>
          <p>{companyName}</p>
        </div>
        <div className="text-center mt-1">
            <p>{companyDetails}</p>
        </div>
      </footer>
    </div>
  );
};

export default PrintableStructureAvailability;
